import React, { useState } from 'react';
import { useEntitlements } from '../hooks/useEntitlements';
import { useAuth } from '../AuthContext';
import { ProGateSkeleton, LoadingButtonContent } from './LoadingComponents';
import { useToast } from '../contexts/ToastContext';
import { createNetworkError, createAuthError, createCheckoutError, createTokenError, parseErrorMessage, createRetryableAction } from '../utils/errorHandling';

interface ProGateProps {
  access: 'free' | 'plus' | 'pro';
  children: React.ReactNode;
  source?: 'chapter' | 'flashcards' | 'audio';
}

interface CheckoutRequest {
  priceId: string;
  productId: 'pro' | 'plus';
  mode: 'payment';
}

interface CheckoutResponse {
  url: string;
}

export function ProGate({ access, children, source }: ProGateProps) {
  const { user, hasPro, hasPlus, loading, error: entitlementsError, retry } = useEntitlements();
  const { signInWithGoogle } = useAuth();
  const { showError, showSuccess, showInfo } = useToast();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If access is free, always render children
  if (access === 'free') {
    return <>{children}</>;
  }

  // Show loading skeleton while entitlements are loading (Requirement 9.1, 9.2)
  if (loading) {
    return <ProGateSkeleton />;
  }

  // If user has Pro access, render children (Pro has access to everything)
  if (hasPro) {
    return <>{children}</>;
  }

  // If user has Plus access
  if (hasPlus) {
    // Plus users get access to Flashcards and Chapters
    if (source === 'flashcards' || source === 'chapter') {
      return <>{children}</>;
    }
    // Plus users are blocked from Audio (and other Pro-only features)
    // Fall through to render lock overlay
  }

  // Determine which upgrade to offer
  // If user has Plus but tries to access Pro content -> Offer Upgrade (300 CZK)
  // If user is Free -> Offer Plus (399 CZK) or Pro (700 CZK) - defaulting to Pro for now in this simple view

  const isUpgrade = hasPlus; // User is upgrading from Plus to Pro
  const priceId = isUpgrade
    ? 'price_1SYS14CR1GWecDhIpueAApV7' // Upgrade price (300 CZK)
    : 'price_1S81xhCR1GWecDhI7CNL2ELF'; // Full Pro price (700 CZK)

  // Create retryable checkout action
  const performCheckout = createRetryableAction(async (idToken: string) => {
    const checkoutRequest: CheckoutRequest = {
      priceId: priceId,
      productId: 'pro', // Always upgrading to Pro here
      mode: 'payment'
    };

    const response = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(checkoutRequest)
    });

    if (!response.ok) {
      if (response.status >= 500) {
        throw createNetworkError('Server je dočasně nedostupný. Zkus to za chvilku.');
      } else if (response.status === 401) {
        throw createTokenError('Přihlas se prosím znovu.');
      } else {
        throw createCheckoutError(`Checkout selhal (${response.status}). Zkus to znovu.`);
      }
    }

    return response.json();
  }, 2, 1500);

  // Handle checkout flow for signed-in users
  const handleCheckout = async () => {
    if (!user) {
      // Requirement 10.1, 10.2: Prompt sign-in for signed-out users with toast
      try {
        showInfo('Přihlašování...', 'Přesměrováváme tě na přihlášení přes Google.');
        await signInWithGoogle();
        showSuccess('Přihlášení úspěšné!', 'Nyní můžeš pokračovat k nákupu.');
        return;
      } catch (error) {
        console.error('Sign-in failed:', error);
        const errorInfo = parseErrorMessage(createAuthError('Přihlášení se nezdařilo. Zkus to prosím znovu.'));
        setError(errorInfo.message);
        showError(errorInfo.title, errorInfo.message);
        return;
      }
    }

    setCheckoutLoading(true);
    setError(null);

    try {
      showInfo('Připravujeme checkout...', 'Chvilku strpení, otevíráme platební bránu.');

      // Get Firebase ID token for authentication (Requirement 2.2)
      const idToken = await user.getIdToken();

      // Make checkout API call with retry logic (Requirement 2.5)
      const data: CheckoutResponse = await performCheckout(idToken);

      showSuccess('Přesměrování na platbu', 'Otevíráme Stripe checkout...');

      // Small delay to show success message
      setTimeout(() => {
        window.location.href = data.url;
      }, 1000);

    } catch (error) {
      console.error('Checkout error:', error);
      const errorInfo = parseErrorMessage(error);
      setError(errorInfo.message);
      showError(errorInfo.title, errorInfo.message);
      setCheckoutLoading(false);
    }
  };

  // Handle sign-in for signed-out users
  const handleSignIn = async () => {
    try {
      showInfo('Přihlašování...', 'Přesměrováváme tě na přihlášení přes Google.');
      await signInWithGoogle();
      showSuccess('Přihlášení úspěšné!', 'Vítej zpět! Nyní máš přístup ke svému účtu.');
    } catch (error) {
      console.error('Sign-in failed:', error);
      const errorInfo = parseErrorMessage(createAuthError('Přihlášení se nezdařilo. Zkus to prosím znovu.'));
      setError(errorInfo.message);
      showError(errorInfo.title, errorInfo.message);
    }
  };

  // Render blurred content preview with lock overlay (Requirement 1.1, 1.2, 14.1)
  return (
    <div className="relative">
      {/* Blurred content preview */}
      <div className="blur-sm pointer-events-none select-none opacity-50">
        {children}
      </div>

      {/* Lock overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-2xl">
        <div className="text-center p-6 max-w-sm">
          {/* Lock icon with glow effect */}
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-blue-600/30 flex items-center justify-center shadow-lg shadow-blue-500/25">
            <svg className="w-8 h-8 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>

          {/* Upgrade messaging (Requirement 1.2) */}
          <h3 className="text-xl font-bold text-white mb-2">
            {isUpgrade ? '🚀 Upgrade na Pro' : '🔒 Obsah pro Pro uživatele'}
          </h3>

          <p className="text-sm text-gray-300 mb-4">
            {isUpgrade
              ? 'Získej přístup k audioknihám a testům'
              : 'Tento obsah je dostupný pouze s verzí Pro'}
          </p>

          <div className="text-xs text-gray-400 mb-6 space-y-1">
            <div>✓ Všechny kapitoly a testy</div>
            <div>✓ Flashcards + opakování chyb</div>
            <div>✓ Audioknihy (offline poslech později)</div>
          </div>

          {/* Error messages */}
          {(error || entitlementsError) && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200 text-sm">
              {error || entitlementsError}
              {entitlementsError && (
                <button
                  onClick={retry}
                  className="ml-2 underline hover:no-underline"
                >
                  Zkusit znovu
                </button>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="space-y-3">
            {user ? (
              // Signed-in user: show upgrade button (Requirement 2.1, 2.4)
              <button
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="w-full rounded-xl px-4 py-3 font-medium font-poppins bg-brand-orange hover:bg-brand-orange/90 disabled:opacity-60 text-white transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-brand-orange/25"
              >
                <LoadingButtonContent
                  isLoading={checkoutLoading}
                  loadingText="Otevírám Checkout…"
                  defaultText={isUpgrade ? "🚀 Upgrade za 300 CZK" : "🚀 Upgrade na Pro"}
                />
              </button>
            ) : (
              // Signed-out user: show both options (Requirement 1.3, 10.1)
              <>
                <button
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                  className="w-full rounded-xl px-4 py-3 font-medium font-poppins bg-brand-orange hover:bg-brand-orange/90 disabled:opacity-60 text-white transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-brand-orange/25"
                >
                  <LoadingButtonContent
                    isLoading={checkoutLoading}
                    loadingText="Otevírám Checkout…"
                    defaultText="🚀 Upgrade na Pro"
                  />
                </button>
                <button
                  onClick={handleSignIn}
                  className="w-full rounded-xl px-4 py-3 font-medium bg-white/10 hover:bg-white/20 text-white transition-colors border border-white/20"
                >
                  Přihlásit přes Google
                </button>
              </>
            )}
          </div>

          {/* Trust indicators (Requirement 1.4) */}
          <div className="mt-4 text-xs text-gray-400">
            🔒 Platba přes Stripe • Bezpečné
          </div>
        </div>
      </div>
    </div>
  );
}